import {
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {
  EpisodeScheduleType,
  ExtendedProgramGuideAction,
  ExtendedProgramGuideActionType,
  ExtendedProgramGuideService,
  IEpisode,
  IInputEpisode,
  cancelChannelEpisodesEditing,
  updateChannel
} from '../../../../core/services/extended-program-guide.service';
import { EpisodeDetailsDialogComponent } from '../episode-details-dialog/episode-details-dialog.component';
import { DateTime, Duration, Interval } from 'luxon';
import {
  DialogService,
  IDialog,
  IDialogOptions
} from '../../../../shared/secured-shared/dialog/dialog.service';
import { Subscription, finalize } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ChannelsResponse } from 'src/app/core/services/channels.service';
import { IDayProgram } from '../schedule/schedule-program/schedule-program.component';
import { LogoLibraryResponse } from 'src/app/core/interfaces/logoLibraryResponse';
import { ConfirmationDialogService } from 'src/app/shared/secured-shared/confirmation-dialog/confirmation-dialog.service';
import { DIALOG_FRAME_TEST_IDS } from 'src/app/shared/secured-shared/confirmation-dialog/confirmation-dialog.models';
import calculateEpisodePosition from './utils/calculateEpisodePosition';
import generateDayScheduleMap from './utils/generateDayScheduleMap';
import AnimationFrame from 'src/app/core/utils/AnimationFrame';

interface ITimeSpot {
  /**
   * Start and end of day
   */
  day: Interval;
  /**
   * Start and end time of the time spot
   */
  range: Interval;
  label: string;
  positioningInformation: {
    /**
     * Distance since the last time spot
     */
    distance: number;
    width: number;
    /**
     * Distance since the start of the day
     */
    shift: number;
  };
  isCurrentHour?: boolean;
}

/**
 * The schedule of episodes for an entire day
 */
interface IDaySchedule {
  episodes: IDayProgram[];
  /**
   * range of the entire day (from start of the day, to the end of it)
   */
  range: Interval;
  /**
   * weekday name
   */
  weekDay: string;
  /**
   * month name
   */
  month: string;
  /**
   * day number
   */
  day: string;
  /**
   * year number
   */
  year: string;
}

@Component({
    selector: 'app-episodes-calendar-view',
    templateUrl: './episodes-calendar-view.component.html',
    styleUrls: ['./episodes-calendar-view.component.scss'],
    standalone: false
})
export class EpisodesCalendarViewComponent
  implements OnInit, AfterViewInit, OnDestroy, IDialog, AfterViewChecked
{
  /**
   * Episodes to show on the calendar view
   */
  private episodes: IEpisode[] = [];
  /**
   * Timer to update the time spots positions on the calendar view
   */
  private updateSpotsTimer: number | null = null;
  /**
   * Max width in pixels for each day row in the calendar view
   */
  public rowWidth = 6000;
  /**
   * Episodes transformed into a list of days, each day containing a list of episodes. This is where we transform
   * recurrent episodes into a list of episodes with certain ranges that is
   */
  public scheduleList: IDaySchedule[] = [];
  public timeSpots: ITimeSpot[] = [];
  public isGettingEpisodes = false;
  public isScrolling = false;
  /**
   * Distance between each time slot
   */
  private timeSpotDistance = Duration.fromObject({
    hour: 1
  });

  /**
   * Reference to the 12 PM indicator
   */
  @ViewChild('noonLineIndicator')
  private noonLineIndicator: ElementRef<HTMLDivElement>;

  /**
   * Reference to the entire time slots element, not each individual time slot
   */
  @ViewChild('timeSlots')
  public timeSlotsElementRef: ElementRef<HTMLDivElement>;
  @ViewChild('scrollView') private scrollViewRef: ElementRef<HTMLDivElement>;
  /**
   * Vertical cells that stay inside the time spot
   */
  @ViewChildren('timeSpotVerticalCell')
  private timeSpotVerticalCells: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('calendarVerticalCell')
  private calendarVerticalCellList: QueryList<ElementRef<HTMLDivElement>>;
  @ViewChildren('episodeRowContents') private episodeRowContents: QueryList<
    ElementRef<HTMLDivElement>
  >;
  @ViewChildren('episodeRow') private episodeRows: QueryList<
    ElementRef<HTMLDivElement>
  >;
  @ViewChildren('timeSpot') private timeSpotReferences: QueryList<
    ElementRef<HTMLDivElement>
  >;
  @ViewChildren('timeSpotLabel') private timeSpotLabels: QueryList<
    ElementRef<HTMLDivElement>
  >;
  /**
   * List of day info elements. They do not stay within the row of the day, but these items
   * are used to be positioned absolutely on the left of the scroll view when the user
   * is scrolling horizontally.
   */
  @ViewChildren('scheduleWeekDayBlock')
  private scheduleWeekDayBlocks: QueryList<ElementRef<HTMLDivElement>>;

  @ViewChildren('requestDayInfoWidth')
  private calendarDayInfoWidthRequirementList: QueryList<
    ElementRef<HTMLElement>
  >;

  private lastScheduleList: number | null = null;

  /**
   * We need to display a minimum of 15 days in the calendar view. This number is going to be
   * used to adjust the schedule list to a size
   */
  private minDayScheduleListLength = 15;

  public data: {
    channel: ChannelsResponse;
    episodes: IInputEpisode[];
    logosLibrary: LogoLibraryResponse[];
  };

  public close: (response: IInputEpisode[] | null) => void;
  public dialogOptions: IDialogOptions;

  private readonly animationFrames = {
    update: new AnimationFrame(() => {
      this.update();
    })
  };

  private readonly epgActions: Subscription;

  /**
   * Default episode to be used when creating a new episode
   */
  private readonly defaultEpisode: IInputEpisode = {
    show_type: '',
    title: '',
    name: '',
    starRating: null,
    viewer_rating: '',
    start_date: DateTime.now().toSeconds(),
    quality: 0,
    schedule: {
      type: EpisodeScheduleType.Definite
    },
    duration: 30,
    description: '',
    show_thumbnail_url: null,
    title_thumbnail_url: null,
    options: {
      hasSubtitles: false,
      hasClosedCaptions: false,
      host: null,
      episode: null,
      game: null
    }
  };

  /**
   * The duration since the start of the day where we're going to show the dashed line indicator.
   */
  private dashedLineIndicator = Duration.fromObject({
    hour: 12
  });

  public constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly matSnackbar: MatSnackBar,
    private readonly epgDialogService: DialogService,
    private readonly confirmationDialog: ConfirmationDialogService,
    private readonly extendedProgramGuideService: ExtendedProgramGuideService
  ) {
    this.epgActions = this.extendedProgramGuideService.listen().subscribe({
      next: (action) => {
        this.onAction(action);
      }
    });
  }

  public ngOnInit(): void {
    this.calculateTimeSlots();

    this.dialogOptions.cancelButton.show = true;
    this.dialogOptions.saveButton.show = false;
    this.dialogOptions.deleteButton.show = false;
    this.dialogOptions.cancelButton.label = 'Close';
    this.dialogOptions.deleteButton.label = 'Clear All';

    // /**
    //  * Create 10-day schedule list starting from today for testing vertical day list
    //  */
    // this.scheduleList = [];
    // for (let i = 0; i < 10; i++) {
    //   const startDate = DateTime.now().plus({ days: i }).startOf('day');
    //   const episodes: IDayProgram[] = generateRandomDayEpisodes(startDate);
    //   this.scheduleList.push({
    //     episodes,
    //     year: startDate.toFormat('yyyy'),
    //     month: startDate.toFormat('MMM'),
    //     range: Interval.fromDateTimes(startDate, startDate.endOf('day')),
    //     weekDay: startDate.toFormat('EEE'),
    //     day: startDate.toFormat('d')
    //   });
    // }
    // this.scheduleList.sort((a, b) => {
    //   return a.range.start.toSeconds() - b.range.start.toSeconds();
    // });
  }

  public ngAfterViewInit(): void {
    /**
     * Call it as soon as the view has been initialized.
     *
     * If this is not called, while `getEpisodes` is completed and the episodes are set,
     * the `update` method will be called, the view will be looking bad for the end user.
     */
    this.animationFrames.update.run();

    /**
     * Get the episodes from the server
     */
    this.getEpisodes();
  }

  public ngAfterViewChecked(): void {
    /**
     * Continue to call update up to 2 seconds since the last update to
     * the schedule list.
     *
     * The idea is that `updateScheduleList` is called whenever the episodes
     * are changed, and it sets the `lastScheduleList` property afterwards.
     *
     * Since `ngAfterViewChecked` is called multiple times, we want to avoid
     * calling it multiple times, so we understand that calling `update` for all
     * the calls to `ngAfterViewChecked` since `lastScheduleList` was set, is
     * acceptable as it will fix the synchronization issues between the `scheduleList`,
     * and the episodes while maintaining performance.
     */
    if (
      this.lastScheduleList !== null &&
      Date.now() - this.lastScheduleList <= 2000
    ) {
      this.animationFrames.update.run();
    }
  }

  private onAction(action: ExtendedProgramGuideAction) {
    switch (action.type) {
      case ExtendedProgramGuideActionType.CancelChannelEpisodes:
      case ExtendedProgramGuideActionType.SaveEpisode:
        break;
      case ExtendedProgramGuideActionType.DeleteEpisode:
        this.getEpisodes();
        break;
    }
  }

  /**
   * Fill in the time slots according to the `timeSpotDistance` property present in this component,
   * taking into consideration the entire day time range.
   */
  private calculateTimeSlots() {
    let time = DateTime.local().startOf('day');
    const day = Interval.fromDateTimes(time, time.endOf('day'));

    this.timeSpots.splice(0, this.timeSpots.length);

    if (this.timeSpotDistance.as('milliseconds') <= 0) {
      console.error('The distance between time slots must be greater than 0');
      return;
    }

    while (time.toMillis() < day.end.toMillis()) {
      const label = time.toFormat('hh:mm a');

      const endTime = time.plus(this.timeSpotDistance);

      // Do the calculations of the time spots here, to avoid having to do the calculations everytime
      /**
       * The time spot range
       */
      const range = Interval.fromDateTimes(time, endTime);

      /**
       * Distance from the time spot to the start of the day
       */
      const distance = range.start.diff(day.start, 'milliseconds');

      /**
       * The width the time spot element should have based on the
       * total duration of the day
       */
      const width = range.count('milliseconds') / day.count('milliseconds');

      /**
       * How much of that distance is part of the day in milliseconds
       */
      const shift = distance.as('milliseconds') / day.count('milliseconds');

      /**
       * Check current hour
       */
      const isCurrentHour = time.hour === DateTime.local().hour;

      this.timeSpots.push({
        label,
        range,
        positioningInformation: {
          width,
          distance:
            endTime.diff(range.start, 'milliseconds').milliseconds /
            day.count('milliseconds'),
          shift
        },
        day,
        isCurrentHour
      });

      time = endTime;
    }
  }

  /**
   * Returns the width of the first day info element. If no day info elements are available,
   * we simply return 0, for the sake of rendering the calendar cells, and more.
   * @returns The width of the day info element, or 0 if no days are available
   */
  private findDayInfoElementOffsetWidth() {
    if (!this.scheduleWeekDayBlocks.length) {
      return 0;
    }

    /**
     * Get the width of the day info elements to add as width to the time spots
     */
    return this.scheduleWeekDayBlocks.first.nativeElement.offsetWidth;
  }

  /**
   * Update the elements that depend on the width of day info elements
   */
  private updateDayInfoDependentElements() {
    if (!this.calendarDayInfoWidthRequirementList) {
      return;
    }

    const dayInfoWidth = this.findDayInfoElementOffsetWidth();

    for (const elementRef of this.calendarDayInfoWidthRequirementList) {
      const el = elementRef.nativeElement;
      el.style.width = `${dayInfoWidth}px`;
      el.style.flex = `0 0 ${dayInfoWidth}px`;
    }
  }

  private styleTimeSlotsContainer() {
    if (!this.timeSlotsElementRef) {
      console.error(
        'Failed to style time slots container, because time slots element reference was inaccessible'
      );
      return;
    }

    const el = this.timeSlotsElementRef.nativeElement;

    /**
     * Reset the width of the element before setting it again to avoid
     * getting a potential residual width from a previous size of the calendar.
     */
    el.style.width = '';

    /**
     * Now set the width of the time slots again.
     */
    el.style.width = `${el.scrollWidth}px`;
  }

  // zoom = 0;

  // /**
  //  * Simple zoom implementation for testing calendar possible issues
  //  */
  // public onMouseWheel(e: Event) {
  //   if (!(e instanceof WheelEvent)) {
  //     return;
  //   }
  //   if (!e.ctrlKey) {
  //     return;
  //   }

  //   e.preventDefault();
  //   e.stopPropagation();

  //   const zoomSize: DurationLikeObject = {
  //     minutes: 5
  //   };

  //   let newTimeSpotDistance = this.timeSpotDistance;
  //   let n: number;

  //   if (e.deltaY < 0) {
  //     n = 1;
  //     // zoom in
  //     newTimeSpotDistance = newTimeSpotDistance.minus(zoomSize);
  //   } else if (e.deltaY > 0) {
  //     // zoom out
  //     n = -1;
  //     newTimeSpotDistance = newTimeSpotDistance.plus(zoomSize);
  //   }

  //   /**
  //    * Validate if the selected duration is valid to be distributed inside day. For instance,
  //    * 50-minute time slots are unsuitable since they will overlap the last day.
  //    *
  //    * We check the validity by dividing the entire day in milliseconds by the given duration
  //    * by milliseconds. If it returns a fractioned number, then the duration will provoke uneven
  //    * distribution of the time slots.
  //    *
  //    * @param duration Distance between time slots
  //    * @returns false if the selected distance is invalid to fit a day, true if it is valid
  //    */
  //   function isValidTimeSlotDistance(duration: Duration) {
  //     return (86400000 / duration.as('milliseconds')) % 1 === 0;
  //   }

  //   /**
  //    * Find the next appropriate time slot to make sure it doesn't overlap the current day
  //    */
  //   while (
  //     !isValidTimeSlotDistance(newTimeSpotDistance) &&
  //     newTimeSpotDistance.as('milliseconds') <= 86400000
  //   ) {
  //     if (n === 1) {
  //       newTimeSpotDistance = newTimeSpotDistance.minus({
  //         minutes: 1
  //       });
  //     } else {
  //       newTimeSpotDistance = newTimeSpotDistance.plus({
  //         minutes: 1
  //       });
  //     }
  //   }

  //   if (
  //     newTimeSpotDistance.as('minutes') >= 15 &&
  //     isValidTimeSlotDistance(newTimeSpotDistance)
  //   ) {
  //     this.timeSpotDistance = newTimeSpotDistance;
  //   } else {
  //     console.error(
  //       'There is no time spot for this zoom scale: %d',
  //       newTimeSpotDistance.as('milliseconds')
  //     );
  //   }

  //   const minRowWidth = 6000;
  //   const maxRowWidth = 12000;
  //   const zoomIncrease = (1 / 40) * n;

  //   this.zoom = Math.max(0, this.zoom + zoomIncrease);
  //   this.rowWidth = minRowWidth + this.zoom * (maxRowWidth - minRowWidth);

  //   /**
  //    * Calculate time slots again since we are changing the `timeSpotDistance` property
  //    */
  //   this.calculateTimeSlots();

  //   /**
  //    * Sync to update the entire calendar
  //    */
  //   this.sync();
  // }

  /**
   * Position the vertical cells of the time slots. If `ignoreDashedLine` is true,
   * we do not position the vertical line if the current spot is the same as the one
   * defined in the `dashedLineIndicator` property of this component.
   * @param cellList Cell list query list
   * @param container Element that's going have its height used for each cell
   * @param ignoreDashedLine Whether to ignore the dashed line indicator
   * @returns
   */
  private positionVerticalCells(
    cellList: QueryList<ElementRef<HTMLDivElement>>,
    container: ElementRef<HTMLDivElement>,
    ignoreDashedLine: boolean
  ) {
    if (!cellList) {
      console.error(
        'Failed to position cells, ' + 'because cells element were inaccessible'
      );
      return;
    }
    if (!container) {
      console.error(
        'Failed to set time slot cell height, ' +
          'because scroll view element was inaccessible'
      );
      return;
    }
    const dayInfoElementOffsetWidth = this.findDayInfoElementOffsetWidth();
    if (dayInfoElementOffsetWidth === null) {
      console.error(
        'Failed to set time slot cell height, ' +
          'because day info element offset width was inaccessible'
      );
      return;
    }

    const containerEl = container.nativeElement;

    /**
     * Reset the height of all cell elements so the scroll view height
     * can be adjusted accordingly.
     */
    for (const cell of cellList) {
      cell.nativeElement.style.height = '';
    }

    /**
     * Scroll view scroll height
     */
    const height = containerEl.scrollHeight;

    let i = 0;
    let left = dayInfoElementOffsetWidth;
    for (const cellRef of cellList) {
      const spotInfo = this.timeSpots[i];
      // It can happen when the zoom feature is implemented
      if (!spotInfo) {
        console.error(
          `There is no time spot for time slot cell at index: ${i}`
        );
        break;
      }

      /**
       * Difference between the start of the time spot and the start of the day
       */
      const diff = spotInfo.range.end.diff(spotInfo.day.start, 'milliseconds');

      /**
       * Increase `left` value accordingly
       */
      left += spotInfo.positioningInformation.distance * this.rowWidth;

      const el = cellRef.nativeElement;

      /**
       * If current cell element is at the same position of the day as the current cell, hide the cell
       */
      if (
        !ignoreDashedLine &&
        diff.milliseconds === this.dashedLineIndicator.as('milliseconds')
      ) {
        el.style.display = 'none';
      } else {
        el.style.display = '';
      }
      /**
       * Set the position of the cell inside the calendar view considering that it is
       * coming out of the left-most part of the element.
       */
      el.style.transform = `translate3d(${left}px, 0, 0)`;
      el.style.height = `${height}px`;
      i++;
    }
  }

  private positionTimeSpots() {
    if (!this.timeSpotLabels) {
      console.error(
        'Failed to position time spots, because time spot labels were inaccessible'
      );
      return;
    }

    if (!this.timeSpotReferences) {
      console.error(
        'Failed to position time spots, because time spots references were inaccessible'
      );
      return;
    }

    // This is better because it considers border width of the day info element
    // const dayInfoElementOffsetWidth = this.findDayInfoElementOffsetWidth();

    let i = 0;

    for (const ref of this.timeSpotReferences) {
      const el = ref.nativeElement;
      const { positioningInformation } = this.timeSpots[i];

      /**
       * Final position of the time spot element in pixels
       */
      const left = positioningInformation.shift * this.rowWidth;

      el.style.width = `${positioningInformation.width * this.rowWidth}px`;
      el.style.transform = `translate3d(${left}px,0,0)`;

      i++;
    }
  }

  private updateEpisodeRows() {
    if (!this.episodeRows) {
      return;
    }
    /**
     * Reset the width of each episode row
     */
    for (const row of this.episodeRows) {
      const rowEl = row.nativeElement;
      rowEl.style.width = '';
    }
    /**
     * Calculate it again
     */
    for (const row of this.episodeRows) {
      const rowEl = row.nativeElement;
      rowEl.style.width = `${rowEl.scrollWidth}px`;
    }
  }

  public ngOnDestroy(): void {
    if (this.updateSpotsTimer !== null) {
      clearTimeout(this.updateSpotsTimer);
    }
    this.epgActions.unsubscribe();
    /**
     * Cancel scheduled update to elements
     */
    this.animationFrames.update.cancel();
  }

  isOpeningAddEpisodeDialog = false;

  public openEditEpisodeDetailsDialog(episode: IEpisode) {
    const channelId = this.data.channel.id;
    if (!channelId) {
      this.matSnackbar.open(
        'No channel id found. It is not possible to edit an episode.',
        'Ok'
      );
      return;
    }
    this.epgDialogService
      .openDialog({
        content: EpisodeDetailsDialogComponent,
        title: 'Show Details',
        data: {
          channelId: channelId,
          episodes: this.episodes,
          id: episode.id,
          episode
        }
      })
      .afterClosed()
      .subscribe((newEpisode) => {
        if (newEpisode === null) {
          return;
        }
        this.extendedProgramGuideService
          .editEpisode(channelId, episode.id, newEpisode)
          .subscribe({
            next: (result) => {
              this.episodes = this.episodes.map((obj) => {
                if (obj.id === episode.id) {
                  return {
                    id: episode.id,
                    ...newEpisode
                  };
                } else {
                  return obj;
                }
              });
              this.sync();
            },
            error: (err) => {
              console.error(err);
              this.matSnackbar.open('Failed to edit episode', 'Ok');
            }
          });
      });
  }

  public openAddEpisodeDialog(): void {
    const channelId = this.data.channel.id;
    if (!channelId) {
      this.matSnackbar.open(
        'No channel id found. It is not possible to create an episode.',
        'Ok'
      );
      return;
    }
    this.isOpeningAddEpisodeDialog = true;
    this.epgDialogService
      .openDialog({
        title: 'Add Episode',
        content: EpisodeDetailsDialogComponent,
        data: {
          id: null,
          episodes: this.episodes,
          channelId,
          episode: this.defaultEpisode
        }
      })
      .afterClosed()
      .pipe(
        finalize(() => {
          this.isOpeningAddEpisodeDialog = false;
        })
      )
      .subscribe((newEpisode) => {
        if (newEpisode === null) {
          return;
        }
        this.extendedProgramGuideService
          .createEpisode(channelId, newEpisode)
          .subscribe({
            next: (result) => {
              this.episodes.push({
                id: result.id,
                ...newEpisode
              });
              this.sync();
            },
            error: (err) => {
              console.error(err);
              this.matSnackbar.open('Failed to create episode', 'Ok');
            }
          });
      });
  }

  /**
   * This function will indirectly call the `update` function, while recreating the
   * `scheduleList` array beforehand.
   *
   * Use it whenever a change is performed in the episodes.
   */
  private sync() {
    this.updateScheduleList();
    this.lastScheduleList = Date.now();
  }

  /**
   * This method recreates the entire schedule list.
   *
   * Do not call this method directly. Use `sync` method instead.
   */
  private updateScheduleList() {
    const dayScheduleMap = generateDayScheduleMap(this.episodes);

    const newScheduleList = new Array<IDaySchedule>();

    /**
     * Calculate the difference between the available episode list and the minimum day schedule list length
     */
    const diff = dayScheduleMap.size - this.minDayScheduleListLength;

    /**
     * If the difference between the current day schedule list length and the minimum day schedule list length
     * is less than 0, we need to add more days to the list.
     */
    if (diff < 0) {
      const list = Array.from(dayScheduleMap.values());
      /**
       * Amount of days to add
       */
      const amount = -diff;
      let currentStartDay: DateTime =
        list.length > 0
          ? list[list.length - 1].day.start
          : DateTime.now().startOf('day');
      for (let i = 0; i < amount; i++) {
        currentStartDay = currentStartDay.plus({ days: 1 });
        const day = Interval.fromDateTimes(
          currentStartDay,
          currentStartDay.endOf('day')
        );
        dayScheduleMap.set(day.start.toSeconds(), {
          day,
          items: []
        });
      }
    }

    for (const scheduleRow of dayScheduleMap.values()) {
      const programs = new Array<IDayProgram>();
      newScheduleList.push({
        episodes: programs,
        year: scheduleRow.day.start.toFormat('yyyy'),
        month: scheduleRow.day.start.toFormat('MMM'),
        range: scheduleRow.day,
        weekDay: scheduleRow.day.start.toFormat('EEE'),
        day: scheduleRow.day.start.toFormat('d')
      });
      for (const { program, range } of scheduleRow.items) {
        programs.push({
          name: program.title,
          program,
          position: calculateEpisodePosition({
            episode: {
              range
            },
            day: scheduleRow.day
          }),
          formatted: {
            range: range.toFormat('HH:mm')
          },
          description: program.description,
          range
        });
      }
    }

    this.scheduleList = newScheduleList;
  }

  /**
   * Do not call this method directly. Use `sync` method instead.
   */
  private update() {
    this.updateEpisodeRows();

    this.positionTimeSpots();

    this.styleTimeSlotsContainer();

    this.positionVerticalCells(
      this.timeSpotVerticalCells,
      this.timeSlotsElementRef,
      true
    );

    this.positionVerticalCells(
      this.calendarVerticalCellList,
      this.scrollViewRef,
      false
    );

    this.updateDayInfoDependentElements();

    this.positionNoonLine();
  }

  public onDelete() {
    this.confirmationDialog
      .confirm({
        title: 'Clear all episodes confirmation',
        message:
          'Are you sure you want to delete all the episodes of this channel? This action cannot be undone.',
        confirmLabel: 'Delete',
        destructive: true,
        testIds: DIALOG_FRAME_TEST_IDS
      })
      .subscribe((result) => {
        if (!result) {
          return;
        }

        this.extendedProgramGuideService
          .clearChannelEpisodes(this.data.channel.id)
          .subscribe({
            next: () => {
              this.matSnackbar.open('All episodes have been deleted', 'Ok');
              this.episodes = [];
              this.sync();
            },
            error: (err) => {
              console.error(err);
              this.matSnackbar.open('Failed to clear episodes', 'Ok');
            }
          });
      });
  }

  public onConfirm() {}

  private getEpisodes() {
    if (this.isGettingEpisodes) {
      return;
    }
    this.isGettingEpisodes = true;
    this.extendedProgramGuideService
      .getEpisodes(this.data.channel.id)
      .subscribe({
        next: (episodes) => {
          this.episodes = Array.from(episodes);
          this.sync();
        },
        error: (err) => {
          console.error(err);
          this.matSnackbar.open('Failed to get episodes', 'Ok');
        },
        complete: () => {
          this.isGettingEpisodes = false;
        }
      });
  }

  public save() {
    this.extendedProgramGuideService.broadcastAction(
      updateChannel(this.data.channel.id, this.episodes)
    );
    // ! transform new episodes into IInputEpisode
    this.close(this.data.episodes);
  }

  public onCancel() {
    this.extendedProgramGuideService.broadcastAction(
      cancelChannelEpisodesEditing(this.data.channel.id)
    );
    this.close(null);
  }

  public onTodayClick() {
    if (!this.scrollViewRef) {
      return;
    }
    const scrollView = this.scrollViewRef.nativeElement;
    scrollView.scrollLeft = 0;
    scrollView.scrollTop = 0;
  }

  getDefaultLogoSrc() {
    const channelLogo = this.data.logosLibrary.find(
      (logo) => logo.is_default === true
    );
    return channelLogo?.path;
  }

  getLogoSrc(logoId: string): string {
    const channelLogo = this.data.logosLibrary.find(
      (logo) => logo.id === logoId
    );
    if (channelLogo) {
      return channelLogo.path;
    }
    return this.getDefaultLogoSrc();
  }

  get readOnlyMode() {
    return this.data.channel.is_external;
  }

  private positionNoonLine() {
    if (!this.noonLineIndicator) {
      console.error(
        'Failed to position noon line indicator, ' +
          'because noon line indicator reference was inaccessible'
      );
      return;
    }
    if (!this.scrollViewRef) {
      console.error(
        'Failed to position noon line indicator, ' +
          'because scroll view reference was inaccessible'
      );
      return;
    }
    /**
     * Add the day info width to the left position of the noon line indicator
     */
    let left = this.findDayInfoElementOffsetWidth();
    const el = this.noonLineIndicator.nativeElement;
    const time = DateTime.now().startOf('day').plus(this.dashedLineIndicator);
    const day = Interval.fromDateTimes(time.startOf('day'), time.endOf('day'));
    const distance = time.diff(day.start, 'milliseconds');
    const shift = distance.as('milliseconds') / day.count('milliseconds');
    left += shift * this.rowWidth;
    el.style.transform = `translate3d(${left}px, 0, 0)`;
    el.style.height = `${this.scrollViewRef.nativeElement.scrollHeight}px`;
  }
}
